import React from 'react';
import { bool, object, shape, number, func } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { sendCallMeBackRequest } from '../../ducks/callMeBack.duck';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { propTypes } from '../../util/types';
import { withViewport } from '../../util/contextHelpers';

import config from '../../config';
import {
  SectionForBusinessHero,
  SectionForBusinessBenefits,
  SectionForOwnersSteps,
  SectionContactUs,
  FAQ,
  TestimonialCard,
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
} from '../../components';
import { TopbarContainer } from '..';

import facebookImage from '../../assets/lth-og.jpg';
import twitterImage from '../../assets/lth-twitter.jpg';
import css from './ForBusinessPage.module.css';

export const ForBusinessPageComponent = props => {
  const {
    history,
    intl,
    location,
    scrollingDisabled,
    isAuthenticated,
    viewport,
    currentUser = null,
    onSubmitCallMeBack,
    submitInProgress,
    callMeBackError,
    callMeBackSuccess,
    currentUserListing = null,
    currentUserListingFetched = false,
  } = props;

  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org
  // We are using JSON-LD format
  const siteTitle = config.siteTitle;
  const schemaTitle = intl.formatMessage({ id: 'ForBusinessPage.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage(
    { id: 'ForBusinessPage.schemaDescription' },
    { siteTitle }
  );
  const schemaImage = `${config.canonicalRootURL}${facebookImage}`;

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        { url: `${config.canonicalRootURL}${twitterImage}`, width: 1024, height: 512 },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.heroContainer}>
            <SectionForBusinessHero className={css.hero} history={history} location={location} />
          </div>
          <ul className={css.sections}>
            <li className={css.section}>
              <div className={css.sectionContentFirstChild}>
                <h2 className={css.subheading}>
                  Get more trailer hire customers and increase your revenue
                </h2>
                <div className={css.intro}>
                  <p>
                    We connect trailer hire businesses like yours with individuals and businesses
                    searching for trailers to hire. We make the experience easy for everyone with
                    our simple online booking and payment process.{' '}
                  </p>
                  <p>
                    We are proudly Australian-owned and have over 15,000 active trailer hiring
                    customers nationwide, and we are rapidly growing. Our online booking service is
                    free for trailer hire businesses – think of us as an additional channel for you
                    to promote your trailers without any costs.
                  </p>
                </div>
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <h2 className={css.subheading}>
                  Trailer hire businesses are already reaping the rewards
                </h2>
                <div className={css.intro}>
                  <p>
                    Here’s what Mitra from Noble Trailer in Melbourne has to say about listing his
                    trailers on Local Trailer Hire:
                  </p>
                </div>
                <TestimonialCard
                  name={'Mitra'}
                  location={'Noble Trailer in Melbourne'}
                  review={
                    'We run a successful trailer hire company with a great book of individual and commercial clients. Local Trailer Hire has been able to help us generate more business by attracting brand new customers. I couldn’t recommend them more highly!'
                  }
                />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionForBusinessBenefits />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <h2 className={css.subheading}>Your business profile page</h2>
                <div className={css.intro}>
                  <p>
                    Local Trailer Hire gives your business a dedicated page with a custom link (URL,
                    like{' '}
                    <a href="https://www.localtrailerhire.com.au/u/ozsatelliterentals">
                      www.localtrailerhire.com.au/u/ozsatelliterentals
                    </a>
                    ). Your business profile page includes your photo or logo, information about
                    your business, listings for all your trailers, and a summary of your reviews.
                    From there, your customers can click through to one of your trailers to book!
                  </p>
                  <p>
                    This page allows your business to benefit from the strong visibility and ranking
                    of localtrailerhire.com.au in search engine results. The custom URL and content
                    of the page should contain relevant keywords, which contribute to even better
                    search engine optimisation (SEO) for your business. This means your business
                    profile page is likely to appear high up in search results when potential
                    customers search for trailer rentals in your area.
                  </p>
                  <p>
                    You can share the link to your Local Trailer Hire business profile page on your
                    website, social media platforms, business cards and other marketing materials to
                    drive traffic to your dedicated page, so your customers can benefit from our
                    simple online booking service.
                  </p>
                </div>
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionForOwnersSteps isAuthenticated={isAuthenticated} forBusiness={true} />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <h2 className={css.subheading}>Frequently asked questions</h2>
                <FAQ category={'business'} />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionContactUs
                  onSubmitCallMeBack={onSubmitCallMeBack}
                  submitInProgress={submitInProgress}
                  callMeBackError={callMeBackError}
                  callMeBackSuccess={callMeBackSuccess}
                  currentUser={currentUser}
                  sectionTitle="Contact us"
                />
              </div>
            </li>
          </ul>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

ForBusinessPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUserListing: propTypes.ownListing,
  currentUserListingFetched: bool,
  currentUser: propTypes.currentUser,
  onSubmitCallMeBack: func.isRequired,
  submitInProgress: bool.isRequired,
  callMeBackError: propTypes.error,

  // from withRouter
  history: object.isRequired,
  location: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
};

const mapStateToProps = state => {
  const { currentUser, currentUserListing, currentUserListingFetched } = state.user;
  const { isAuthenticated } = state.Auth;
  const { submitInProgress, callMeBackError, callMeBackSuccess } = state.callMeBack;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    currentUserListing,
    currentUserListingFetched,
    isAuthenticated,
    submitInProgress,
    callMeBackError,
    callMeBackSuccess,
  };
};

const mapDispatchToProps = dispatch => ({
  onSubmitCallMeBack: (name, phoneNumber, reasonForCallBack, otherInfo, email) =>
    dispatch(sendCallMeBackRequest(name, phoneNumber, reasonForCallBack, otherInfo, email)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ForBusinessPage = compose(
  withViewport,
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(ForBusinessPageComponent);

export default ForBusinessPage;
